// src/registered/site-admin/components/Workflow-esg/services/TemplateStorageService.js

import { GenericFileUploader } from "../../../../upload/services/genericFileUploader";

/**
 * ================================================================================================
 * PURPOSE:
 *   Physical storage and retrieval service for ESG Template Management.
 *
 * RESPONSIBILITIES:
 *   - Resolve folder names from selected root ESG taxonomy JSON
 *   - Request reduced "core taxonomy" data from backend/Lambda
 *   - Save templates into the template management bucket
 *   - Save template manifests/index files into the template management bucket
 *   - Retrieve templates via backend/Lambda rather than downloading full S3 payloads in browser
 *
 * DESIGN DECISION:
 *   The browser should NOT download the full root taxonomy JSON if it is large.
 *   Instead, a backend/Lambda reads the selected JSON from S3 and returns only the
 *   "core" taxonomy records required by the template builder.
 *
 * ENV EXPECTATIONS:
 *   REACT_APP_SITE_CLEAN_BUCKET=esg-staging-clean
 *   REACT_APP_SITE_TEMPLATE_BUCKET=esg-template-management
 *   REACT_APP_SITE_TEMPLATE_ELEMENTS_BUCKET=esg-template-elements-management
 *
 * OPTIONAL API ENDPOINT ENVS:
 *   REACT_APP_ESG_CORE_TAXONOMY_ENDPOINT=/api/esg/template-core-taxonomy
 *   REACT_APP_ESG_TEMPLATE_INDEX_ENDPOINT=/api/esg/templates/index
 *   REACT_APP_ESG_TEMPLATE_GET_ENDPOINT=/api/esg/templates/get
 *   REACT_APP_ESG_TEMPLATE_DELETE_ENDPOINT=/api/esg/templates/delete
 *
 * NOTES:
 *   - This service is intentionally root-file aware
 *   - Templates are scoped to a folder derived from the selected root JSON filename
 *   - One template = one JSON file
 * ================================================================================================
 */
class TemplateStorageService {
  constructor(config = {}) {
    this.cleanBucket =
      config.cleanBucket || process.env.REACT_APP_SITE_CLEAN_BUCKET;

    this.templateBucket =
      config.templateBucket || process.env.REACT_APP_SITE_TEMPLATE_BUCKET;

    this.templateElementsBucket =
      config.templateElementsBucket ||
      process.env.REACT_APP_SITE_TEMPLATE_ELEMENTS_BUCKET;

    this.coreTaxonomyEndpoint =
      config.coreTaxonomyEndpoint ||
      process.env.REACT_APP_ESG_CORE_TAXONOMY_ENDPOINT ||
      "/api/esg/template-core-taxonomy";

    this.templateIndexEndpoint =
      config.templateIndexEndpoint ||
      process.env.REACT_APP_ESG_TEMPLATE_INDEX_ENDPOINT ||
      "/api/esg/templates/index";

    this.templateGetEndpoint =
      config.templateGetEndpoint ||
      process.env.REACT_APP_ESG_TEMPLATE_GET_ENDPOINT ||
      "/api/esg/templates/get";

    this.templateDeleteEndpoint =
      config.templateDeleteEndpoint ||
      process.env.REACT_APP_ESG_TEMPLATE_DELETE_ENDPOINT ||
      "/api/esg/templates/delete";
  }

  // ===============================================================================================
  // ROOT FILE / TAXONOMY METHODS
  // ===============================================================================================

  /**
   * Build the reduced taxonomy payload needed by the template builder.
   * This does NOT download the full root JSON into the browser.
   * Instead, the backend/Lambda reads the S3 JSON and returns only the core records.
   *
   * @param {Object} rootFileContext
   * @returns {Promise<Object>}
   */
  async getCoreTaxonomyForRootFile(rootFileContext) {
    this.#requireRootFileContext(rootFileContext);

    const payload = {
      bucketName: rootFileContext.bucketName || this.cleanBucket,
      fileName: rootFileContext.fileName,
      filePath: rootFileContext.filePath || "",
      folderName: rootFileContext.folderName || this.#deriveFolderName(rootFileContext.fileName),
    };

    return await this.#postJson(this.coreTaxonomyEndpoint, payload);
  }

  // ===============================================================================================
  // TEMPLATE METHODS
  // ===============================================================================================

  /**
   * Load the template manifest/index for the selected root file context.
   * Expected response shape:
   * {
   *   success: true,
   *   items: [{ id, name, description, templateType, fileName, objectKey, updatedAt, isActive }]
   * }
   *
   * @param {Object} rootFileContext
   * @returns {Promise<Array>}
   */
  async getAllTemplates(rootFileContext) {
    this.#requireRootFileContext(rootFileContext);

    const payload = {
      bucketName: this.templateBucket,
      rootFileContext: this.#normalizeRootFileContext(rootFileContext),
      folderName: this.#buildTemplateFolderName(rootFileContext),
      indexFileName: "templates.index.json",
    };

    const response = await this.#postJson(this.templateIndexEndpoint, payload);
    const items = response?.items || response?.templates || [];

    return Array.isArray(items) ? items : [];
  }

  /**
   * Load one template by ID.
   * This assumes backend/Lambda resolves the template file from the folder + ID.
   *
   * @param {Object} rootFileContext
   * @param {string} templateId
   * @returns {Promise<Object|null>}
   */
  async getTemplateById(rootFileContext, templateId) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireString(templateId, "Template ID is required.");

    const payload = {
      bucketName: this.templateBucket,
      rootFileContext: this.#normalizeRootFileContext(rootFileContext),
      folderName: this.#buildTemplateFolderName(rootFileContext),
      templateId,
      fileName: `${templateId}.json`,
    };

    const response = await this.#postJson(this.templateGetEndpoint, payload);

    return response?.template || response || null;
  }

  /**
   * Save a new template JSON and refresh manifest/index.
   *
   * @param {Object} rootFileContext
   * @param {Object} templateDto
   * @returns {Promise<Object>}
   */
  async saveTemplate(rootFileContext, templateDto) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireObject(templateDto, "Template payload is required.");

    const normalizedTemplate = {
      ...templateDto,
      id: templateDto.id || this.#generateId("tmpl"),
      rootFileName: rootFileContext.fileName,
      rootFolderName: this.#buildTemplateFolderName(rootFileContext),
      updatedAt: new Date().toISOString(),
    };

    const folderName = this.#buildTemplateFolderName(rootFileContext);
    const fileName = `${normalizedTemplate.id}.json`;
    const objectKey = `${folderName}/${fileName}`;

    // 1. Upload the template file
    await this.#uploadJsonToBucket(
      normalizedTemplate,
      this.templateBucket,
      objectKey,
      [
        "ESG",
        "TemplateManagement",
        "Template",
        folderName,
      ],
    );

    // 2. Refresh and persist manifest
    const existingIndex = await this.#safeLoadTemplateIndex(rootFileContext);
    const nextIndex = this.#upsertTemplateIndexEntry(existingIndex, {
      id: normalizedTemplate.id,
      name: normalizedTemplate.name || "",
      description: normalizedTemplate.description || "",
      templateType: normalizedTemplate.templateType || "",
      appliesTo: normalizedTemplate.appliesTo || "",
      reportingFrequency: normalizedTemplate.reportingFrequency || "",
      version: normalizedTemplate.version || 1,
      isActive: normalizedTemplate.isActive ?? true,
      status: normalizedTemplate.status || "draft",
      fileName,
      objectKey,
      updatedAt: normalizedTemplate.updatedAt,
      rootFileName: rootFileContext.fileName,
      folderName,
    });

    await this.#saveTemplateIndex(rootFileContext, nextIndex);

    return normalizedTemplate;
  }

  /**
   * Update an existing template JSON and refresh manifest/index.
   *
   * @param {Object} rootFileContext
   * @param {string} templateId
   * @param {Object} templateDto
   * @returns {Promise<Object>}
   */
  async updateTemplate(rootFileContext, templateId, templateDto) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireString(templateId, "Template ID is required.");
    this.#requireObject(templateDto, "Template payload is required.");

    const folderName = this.#buildTemplateFolderName(rootFileContext);
    const fileName = `${templateId}.json`;
    const objectKey = `${folderName}/${fileName}`;

    const updatedTemplate = {
      ...templateDto,
      id: templateId,
      rootFileName: rootFileContext.fileName,
      rootFolderName: folderName,
      updatedAt: new Date().toISOString(),
    };

    // 1. Upload updated template file
    await this.#uploadJsonToBucket(
      updatedTemplate,
      this.templateBucket,
      objectKey,
      [
        "ESG",
        "TemplateManagement",
        "Template",
        folderName,
      ],
    );

    // 2. Refresh and persist manifest
    const existingIndex = await this.#safeLoadTemplateIndex(rootFileContext);
    const nextIndex = this.#upsertTemplateIndexEntry(existingIndex, {
      id: updatedTemplate.id,
      name: updatedTemplate.name || "",
      description: updatedTemplate.description || "",
      templateType: updatedTemplate.templateType || "",
      appliesTo: updatedTemplate.appliesTo || "",
      reportingFrequency: updatedTemplate.reportingFrequency || "",
      version: updatedTemplate.version || 1,
      isActive: updatedTemplate.isActive ?? true,
      status: updatedTemplate.status || "draft",
      fileName,
      objectKey,
      updatedAt: updatedTemplate.updatedAt,
      rootFileName: rootFileContext.fileName,
      folderName,
    });

    await this.#saveTemplateIndex(rootFileContext, nextIndex);

    return updatedTemplate;
  }

  /**
   * Soft delete from the manifest and optionally notify backend for physical delete.
   *
   * @param {Object} rootFileContext
   * @param {string} templateId
   * @returns {Promise<boolean>}
   */
  async deleteTemplate(rootFileContext, templateId) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireString(templateId, "Template ID is required.");

    const existingIndex = await this.#safeLoadTemplateIndex(rootFileContext);
    const filteredItems = existingIndex.items.filter((item) => item.id !== templateId);

    const nextIndex = {
      ...existingIndex,
      updatedAt: new Date().toISOString(),
      items: filteredItems,
    };

    await this.#saveTemplateIndex(rootFileContext, nextIndex);

    // Optional backend delete hook
    try {
      await this.#postJson(this.templateDeleteEndpoint, {
        bucketName: this.templateBucket,
        rootFileContext: this.#normalizeRootFileContext(rootFileContext),
        folderName: this.#buildTemplateFolderName(rootFileContext),
        templateId,
        fileName: `${templateId}.json`,
      });
    } catch (err) {
      // Deliberately do not fail the workflow if backend hard-delete is unavailable.
      console.warn(
        "⚠️ [TemplateStorageService] Physical delete endpoint failed or is unavailable:",
        err?.message || err,
      );
    }

    return true;
  }

  // ===============================================================================================
  // INDEX / MANIFEST HELPERS
  // ===============================================================================================

  /**
   * Safely load the current template index. Returns an empty index if none exists.
   *
   * @param {Object} rootFileContext
   * @returns {Promise<Object>}
   */
  async #safeLoadTemplateIndex(rootFileContext) {
    try {
      const templates = await this.getAllTemplates(rootFileContext);

      return {
        rootFileName: rootFileContext.fileName,
        folderName: this.#buildTemplateFolderName(rootFileContext),
        updatedAt: new Date().toISOString(),
        items: Array.isArray(templates) ? templates : [],
      };
    } catch (err) {
      console.warn(
        "⚠️ [TemplateStorageService] No existing template index found. Creating a new one.",
      );

      return {
        rootFileName: rootFileContext.fileName,
        folderName: this.#buildTemplateFolderName(rootFileContext),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        items: [],
      };
    }
  }

  /**
   * Save the template manifest/index JSON back to S3.
   *
   * @param {Object} rootFileContext
   * @param {Object} indexPayload
   * @returns {Promise<void>}
   */
  async #saveTemplateIndex(rootFileContext, indexPayload) {
    const folderName = this.#buildTemplateFolderName(rootFileContext);
    const objectKey = `${folderName}/templates.index.json`;

    await this.#uploadJsonToBucket(
      indexPayload,
      this.templateBucket,
      objectKey,
      [
        "ESG",
        "TemplateManagement",
        "TemplateIndex",
        folderName,
      ],
    );
  }

  /**
   * Upsert an index entry for a template.
   *
   * @param {Object} existingIndex
   * @param {Object} templateSummary
   * @returns {Object}
   */
  #upsertTemplateIndexEntry(existingIndex, templateSummary) {
    const items = Array.isArray(existingIndex?.items) ? [...existingIndex.items] : [];
    const existingIndexPosition = items.findIndex((item) => item.id === templateSummary.id);

    if (existingIndexPosition >= 0) {
      items[existingIndexPosition] = {
        ...items[existingIndexPosition],
        ...templateSummary,
      };
    } else {
      items.push(templateSummary);
    }

    items.sort((a, b) => {
      const left = String(a?.name || "").toLowerCase();
      const right = String(b?.name || "").toLowerCase();
      return left.localeCompare(right);
    });

    return {
      ...existingIndex,
      updatedAt: new Date().toISOString(),
      items,
    };
  }

  // ===============================================================================================
  // ROOT FILE / FOLDER HELPERS
  // ===============================================================================================

  /**
   * Normalize the root file context into a stable object.
   *
   * @param {Object} rootFileContext
   * @returns {Object}
   */
  #normalizeRootFileContext(rootFileContext) {
    return {
      bucketName: rootFileContext.bucketName || this.cleanBucket,
      fileName: rootFileContext.fileName,
      filePath: rootFileContext.filePath || "",
      folderName:
        rootFileContext.folderName ||
        this.#deriveFolderName(rootFileContext.fileName),
    };
  }

  /**
   * Build the folder name used in the template bucket for this selected root file.
   *
   * @param {Object} rootFileContext
   * @returns {string}
   */
  #buildTemplateFolderName(rootFileContext) {
    return (
      rootFileContext.folderName ||
      this.#deriveFolderName(rootFileContext.fileName)
    );
  }

  /**
   * Convert root JSON file name into folder name.
   * Example:
   *   ESG_Taxonomy_Sync_1774775834716.json -> ESG_Taxonomy_Sync_1774775834716
   *
   * @param {string} fileName
   * @returns {string}
   */
  #deriveFolderName(fileName) {
    return String(fileName || "")
      .replace(/\.json$/i, "")
      .replace(/[^a-zA-Z0-9-_]/g, "_")
      .trim();
  }

  // ===============================================================================================
  // NETWORK / UPLOAD HELPERS
  // ===============================================================================================

  /**
   * POST JSON payload to backend/Lambda endpoint.
   *
   * @param {string} endpoint
   * @param {Object} payload
   * @returns {Promise<Object>}
   */
  async #postJson(endpoint, payload) {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      throw new Error(
        `Request failed (${response.status}) for ${endpoint}. ${errorText}`.trim(),
      );
    }

    return await response.json();
  }

  /**
   * Upload generated JSON into the target S3 bucket using the existing uploader abstraction.
   *
   * @param {Object} payload
   * @param {string} bucketName
   * @param {string} objectKey
   * @param {Array<string>} labels
   * @returns {Promise<Object>}
   */
  async #uploadJsonToBucket(payload, bucketName, objectKey, labels = []) {
    const response = await GenericFileUploader.uploadGeneratedData(payload, {
      bucketName,
      fileName: objectKey,
      labels,
      batchType: 1,
    });

    if (!response?.success) {
      throw new Error(
        response?.message || `Upload failed for ${bucketName}/${objectKey}`,
      );
    }

    return response;
  }

  // ===============================================================================================
  // GUARDS
  // ===============================================================================================

  /**
   * Guard for root context.
   *
   * @param {Object} rootFileContext
   */
  #requireRootFileContext(rootFileContext) {
    if (!rootFileContext || typeof rootFileContext !== "object") {
      throw new Error("Root file context is required.");
    }

    if (!rootFileContext.fileName || !String(rootFileContext.fileName).trim()) {
      throw new Error("Root file context must include a fileName.");
    }
  }

  /**
   * Guard for generic object.
   *
   * @param {*} value
   * @param {string} message
   */
  #requireObject(value, message) {
    if (!value || typeof value !== "object") {
      throw new Error(message);
    }
  }

  /**
   * Guard for required strings.
   *
   * @param {*} value
   * @param {string} message
   */
  #requireString(value, message) {
    if (!value || typeof value !== "string" || !value.trim()) {
      throw new Error(message);
    }
  }

  /**
   * Generate IDs safely.
   *
   * @param {string} prefix
   * @returns {string}
   */
  #generateId(prefix = "id") {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }

    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default TemplateStorageService;